import { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { Vote, Player } from '../types';

export interface VoteDetail {
  voterId: string;
  voterName: string;
  targetPlayerId: string;
  targetPlayerName: string;
}

export const useVoteDetails = (roomCode: string | null, roundId: string | null, players: Player[]) => {
  const [voteDetails, setVoteDetails] = useState<VoteDetail[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!roomCode || !roundId || players.length === 0) {
      setVoteDetails([]);
      return;
    }

    const fetchVoteDetails = async () => {
      setLoading(true);
      try {
        const votesRef = collection(db, 'rooms', roomCode, 'rounds', roundId, 'votes');
        const votesSnap = await getDocs(votesRef);
        
        const votes = votesSnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Vote[];

        const details: VoteDetail[] = votes.map(vote => {
          const voter = players.find(p => p.id === vote.voterId);
          const target = players.find(p => p.id === vote.targetPlayerId);
          
          return {
            voterId: vote.voterId,
            voterName: voter?.nickname || 'Desconocido',
            targetPlayerId: vote.targetPlayerId,
            targetPlayerName: target?.nickname || 'Desconocido'
          };
        });

        setVoteDetails(details);
      } catch (error) {
        console.error('Error fetching vote details:', error);
        setVoteDetails([]);
      } finally {
        setLoading(false);
      }
    };

    fetchVoteDetails();
  }, [roomCode, roundId, players]);

  // Get who voted for a specific player
  const getVotersFor = (playerId: string): VoteDetail[] => {
    return voteDetails.filter(detail => detail.targetPlayerId === playerId);
  };

  // Get who a specific player voted for
  const getVoteBy = (playerId: string): VoteDetail | null => {
    return voteDetails.find(detail => detail.voterId === playerId) || null;
  };

  return {
    voteDetails,
    loading,
    getVotersFor,
    getVoteBy
  };
};